var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import ChannelContext from '@jetshop/core/components/ChannelContext';
import UpdateCustomerMutation from '@jetshop/core/data/mutations/updateCustomer.gql';
import logError from '@jetshop/core/helpers/logError';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery } from '@apollo/react-hooks';
import { translations } from './signup-field-translations';
import { useCountrySettings } from '../ChannelContext/useCountrySettings';
export function customerAddressToCustomerUpdateAddressInput({ address, isDeleteId = false, countryCode }) {
    const { country } = address, other = __rest(address, ["country"]);
    delete other.__typename;
    // billingAddress and deliveryAddresses share the same query type, therefor both gets ID
    // Billing address mutation doesn't accept an ID, therefor we need to delete it
    if (isDeleteId && other.id)
        delete other.id;
    return Object.assign(Object.assign({}, other), { countryCode: (country && country.code) || countryCode });
}
export function getAddressInputFieldName(name) {
    if (name === 'country') {
        return 'countryCode';
    }
    return name;
}
export function getDeliveryAddressesInput(customer, countryCode) {
    return customer.deliveryAddresses.map((address) => customerAddressToCustomerUpdateAddressInput({ address, countryCode }));
}
export function getBillingAddressInput({ customer, countryCode }) {
    return customerAddressToCustomerUpdateAddressInput({
        address: customer === null || customer === void 0 ? void 0 : customer.billingAddress,
        isDeleteId: true,
        countryCode
    });
}
export function getCustomerUpdatePreferenceInput(customer, 
/** The currently selected channel culture, in case it does not exist on customer */
culture) {
    const preferences = {
        type: {
            acceptsEmail: customer.communication.acceptsEmail,
            acceptsPostal: customer.communication.acceptsPostal,
            acceptsSms: customer.communication.acceptsSms,
            culture
        }
    };
    if (customer.store) {
        preferences.storeLocationId = customer.store.id;
    }
    if (customer.language) {
        preferences.type.culture = customer.language.culture;
    }
    return preferences;
}
function isPrivateCustomer(customer) {
    return customer.__typename === 'PrivateCustomer';
}
export function getCustomerUpdateInput({ customer, billingAddressFields, culture, countryCode }) {
    return Object.assign(Object.assign({}, (isPrivateCustomer(customer)
        ? { pid: customer.pid }
        : { organizationNumber: customer.organizationNumber })), { billingAddress: getBillingAddressInput({
            customer,
            billingAddressFields,
            countryCode
        }), deliveryAddresses: getDeliveryAddressesInput(customer, countryCode), preferences: getCustomerUpdatePreferenceInput(customer, culture) });
}
export function useCustomerCountrySettings(customer) {
    const { selectedChannel } = useContext(ChannelContext);
    const { countrySettings, selectedCountrySettings, loading } = useCountrySettings();
    const byCountryCode = useCallback(function byCountryCode(countryCode) {
        const settings = countrySettings &&
            countrySettings.find((s) => s.countryCode === countryCode);
        return settings || null;
    }, [countrySettings]);
    const [customerCountrySettings, setCustomerCountrySettings] = useState(customer && customer.billingAddress.country
        ? byCountryCode(customer.billingAddress.country.code)
        : selectedCountrySettings);
    const setCountryCode = useCallback((countryCode) => {
        const settings = byCountryCode(countryCode);
        logError(!settings, `Trying to setCountryCode for unknown countryCode "${countryCode}"`);
        if (settings) {
            setCustomerCountrySettings(settings);
        }
    }, [byCountryCode]);
    useEffect(() => {
        if (!customer) {
            return;
        }
        if (!customer.billingAddress.country) {
            logError(true, `Cutomer billingAddress missing "country". ID: ${customer.id}. Available countries: ${selectedChannel.countries
                .map(({ id }) => id)
                .join('/')}`);
            return;
        }
        setCountryCode(customer.billingAddress.country.code);
    }, [customer, selectedChannel.countries, setCountryCode]);
    return {
        loading,
        countrySettings: customerCountrySettings,
        setCountryCode
    };
}
export function useDeliveryAddressFields({ customer, countrySettings, deliveryAddressId }) {
    const fieldType = (customer === null || customer === void 0 ? void 0 : customer.__typename) === 'BusinessCustomer'
        ? 'businessCustomerFields'
        : 'privateCustomerFields';
    const deliveryAddress = useMemo(() => {
        if (deliveryAddressId === undefined)
            return [];
        const deliveryAddressField = countrySettings &&
            countrySettings[fieldType].find(({ name }) => name === 'deliveryAddress');
        const deliveryAddressIndex = customer.deliveryAddresses.findIndex((a) => a.id === deliveryAddressId);
        const deliveryAddressFields = deliveryAddressField
            ? deliveryAddressField.fields.map((field) => {
                return Object.assign(Object.assign({}, field), { value: customer.deliveryAddresses[deliveryAddressIndex][field.name], label: translations[field.name] });
            })
            : [];
        return deliveryAddressFields;
    }, [countrySettings, customer, deliveryAddressId, fieldType]);
    return deliveryAddress;
}
export function useCustomerDetails({ customer, deliveryAddressId }) {
    const fieldType = (customer === null || customer === void 0 ? void 0 : customer.__typename) === 'BusinessCustomer'
        ? 'businessCustomerFields'
        : 'privateCustomerFields';
    const { countrySettings, setCountryCode, loading: customerCountrySettingsLoading } = useCustomerCountrySettings(customer);
    const billingAddress = useMemo(() => {
        var _a;
        const billingAddressField = countrySettings === null || countrySettings === void 0 ? void 0 : countrySettings[fieldType].find(({ name }) => name === 'billingAddress');
        const billingAddressFields = (_a = billingAddressField === null || billingAddressField === void 0 ? void 0 : billingAddressField.fields.map((field) => {
            var _a;
            return Object.assign(Object.assign({}, field), { value: (_a = customer === null || customer === void 0 ? void 0 : customer.billingAddress) === null || _a === void 0 ? void 0 : _a[field.name], label: translations[field.name] });
        })) !== null && _a !== void 0 ? _a : [];
        return billingAddressFields;
    }, [countrySettings, customer, fieldType]);
    const deliveryAddress = useDeliveryAddressFields({
        customer,
        countrySettings,
        deliveryAddressId
    });
    return {
        countrySettings,
        setCountryCode,
        billingAddress,
        deliveryAddress,
        customerCountrySettingsLoading
    };
}
export function useCustomerQuery({ query, deliveryAddressId }) {
    var _a;
    const { refetch, data, loading, error } = useQuery(query);
    const customer = (_a = data === null || data === void 0 ? void 0 : data.customer) !== null && _a !== void 0 ? _a : null;
    const { countrySettings, setCountryCode, billingAddress, deliveryAddress, customerCountrySettingsLoading } = useCustomerDetails({ customer, deliveryAddressId });
    return {
        refetch,
        loading: loading || customerCountrySettingsLoading,
        error,
        customer,
        countrySettings,
        setCountryCode,
        billingAddress,
        deliveryAddress
    };
}
export function useCustomerUpdateMutation({ customerQuery }) {
    const [mutate] = useMutation(UpdateCustomerMutation);
    const { refetch: refetchCustomer } = useCustomerQuery({
        query: customerQuery
    });
    return function updateCustomerMutationWrapper(input) {
        return Promise.resolve()
            .then(() => mutate({
            variables: {
                input
            }
        }))
            .then(() => refetchCustomer())
            .then(() => null);
    };
}
//# sourceMappingURL=useCustomer.js.map